import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { downloadCSV } from "@/lib/csv";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, PackagePlus } from "lucide-react";

const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
const COLS = [["timestamp", "Received"], ["reference", "Reference"], ["supplier", "Supplier"], ["sku", "SKU"], ["generic_name", "Product"],
  ["batch_no", "Batch"], ["expiry_date", "Expiry"], ["quantity", "Qty"], ["unit_cost", "Unit cost"], ["value", "Value"], ["received_by", "Received by"]];

export const ReceiptsTab = () => {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState({ rows: [] });
  const load = useCallback(() => {
    api.get("/wholesale/receipts", { params: { date_from: from, date_to: to } }).then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  const total = data.rows.reduce((a, r) => a + r.value, 0);

  return (
    <Card className="p-4 shadow-sm">
      <p className="text-sm text-muted-foreground mb-3">Goods received from suppliers (Procurement → Purchase orders → Receive, or Stock → Receive) into <b className="text-foreground">{data.warehouse?.name || "the main warehouse"}</b> update its stock automatically and appear here.</p>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div><Label className="text-xs">From</Label><Input type="date" className="w-40 h-9" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ws-receipts-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" className="w-40 h-9" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ws-receipts-to" /></div>
        <div className="text-sm ml-auto">{data.rows.length} receipt line(s) · <span className="font-mono font-semibold">{fmtGHS(total)}</span></div>
        <Button variant="outline" disabled={!data.rows.length} data-testid="ws-receipts-csv-btn"
          onClick={() => downloadCSV(`partner_supply_receipts_${from}_${to}.csv`, data.rows, COLS.map(([key, label]) => ({ key, label })))}>
          <Download className="h-4 w-4 mr-1.5" /> CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr><th className="py-2.5 px-3">Received</th><th className="py-2.5 px-3">Ref / supplier</th><th className="py-2.5 px-3">Product</th><th className="py-2.5 px-3">Batch · expiry</th>
              <th className="py-2.5 px-3 text-right">Qty</th><th className="py-2.5 px-3 text-right">Value (cost)</th></tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground"><PackagePlus className="h-8 w-8 mx-auto mb-2 opacity-40" />No supplier receipts in this period</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60">
                <td className="py-2 px-3 text-xs text-muted-foreground">{fmtDateTime(r.timestamp)}<div>{r.received_by}</div></td>
                <td className="py-2 px-3"><span className="font-mono text-xs">{r.reference || "—"}</span><div className="text-xs text-muted-foreground">{r.supplier}</div></td>
                <td className="py-2 px-3">{r.generic_name} <span className="font-mono text-xs text-muted-foreground">{r.sku}</span></td>
                <td className="py-2 px-3 text-xs">{r.batch_no} · {r.expiry_date}</td>
                <td className="py-2 px-3 text-right font-mono">{r.quantity}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
